import { createContext, useContext, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type ChoiceGroupMode = 'single' | 'multiple'

const ChoiceGroupContext = createContext<ChoiceGroupMode | null>(null)

interface ChoiceGroupProps extends Omit<ComponentProps<'div'>, 'role'> {
  mode?: ChoiceGroupMode
  label: string
}

export function ChoiceGroup({ mode = 'single', label, className, onKeyDown, children, ...props }: ChoiceGroupProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return
    const choices = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-choice-tile="true"]')]
      .filter((choice) => !choice.disabled)
    if (choices.length === 0) throw new Error(`Choice group "${label}" has no enabled choices`)
    const currentIndex = choices.indexOf(document.activeElement as HTMLButtonElement)
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? choices.length - 1
        : event.key === 'ArrowRight' || event.key === 'ArrowDown'
          ? (Math.max(0, currentIndex) + 1) % choices.length
          : (currentIndex <= 0 ? choices.length : currentIndex) - 1
    const next = choices[nextIndex]
    if (!next) throw new Error(`Choice group "${label}" resolved an invalid keyboard target`)
    event.preventDefault()
    next.focus()
    if (mode === 'single') next.click()
  }

  return (
    <ChoiceGroupContext.Provider value={mode}>
      <div
        role={mode === 'single' ? 'radiogroup' : 'group'}
        aria-label={label}
        className={cn('grid gap-2', className)}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </div>
    </ChoiceGroupContext.Provider>
  )
}

interface ChoiceTileProps extends Omit<ComponentProps<'button'>, 'aria-label' | 'children'> {
  ariaLabel: string
  label?: string
  illustration: ReactNode
  selected?: boolean
  controlRole?: 'radio' | 'switch' | 'button'
}

export function ChoiceTile({
  ariaLabel,
  label,
  illustration,
  selected = false,
  controlRole,
  className,
  ...props
}: ChoiceTileProps) {
  const groupMode = useContext(ChoiceGroupContext)
  const role = controlRole ?? (groupMode === 'single' ? 'radio' : 'button')
  return (
    <button
      type="button"
      role={role}
      aria-label={ariaLabel}
      aria-checked={role === 'radio' || role === 'switch' ? selected : undefined}
      aria-pressed={role === 'button' ? selected : undefined}
      data-choice-tile="true"
      data-state={selected ? 'on' : 'off'}
      className={cn(
        'group/choice flex min-w-0 flex-col items-center justify-center gap-1.5 rounded-xl border bg-background px-2 py-2 text-center outline-offset-2 transition',
        'hover:border-foreground/40 hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-45',
        selected && 'border-primary bg-primary/10 ring-2 ring-primary/35',
        className,
      )}
      {...props}
    >
      <span className="flex size-12 items-center justify-center [@media(pointer:coarse)]:size-14" aria-hidden="true">
        {illustration}
      </span>
      {label ? <span className="max-w-full truncate text-xs font-medium">{label}</span> : null}
    </button>
  )
}
